// Renderização visual de um template WhatsApp Business — Header, Body,
// Footer e Buttons. Reutilizado entre o configurador do nó "WhatsApp
// Oficial" (Automacoes) e o TemplateSelectorModal (Inbox).
//
// Estrutura esperada do template (vinda do Meta Graph API,
// persistida em meta_whatsapp_templates.components jsonb):
//   components: Array<{
//     type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS',
//     format?: 'TEXT'|'IMAGE'|'VIDEO'|'DOCUMENT'|'LOCATION',  // só HEADER
//     text?: string,                                          // HEADER text/BODY/FOOTER
//     example?: { ... },
//     buttons?: Array<{ type, text, url?, phone_number?, ... }>
//   }>

import { Image as ImageIcon, Video, FileText, MapPin, ExternalLink, Phone, Copy, MessageSquare } from 'lucide-react';

function substituirVariaveis(texto, variaveis) {
  if (!texto || typeof texto !== 'string') return '';
  return texto.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const valor = variaveis?.[n];
    return valor !== undefined && valor !== null && valor !== ''
      ? String(valor)
      : `{{${n}}}`;
  });
}

function HeaderBlock({ header, variaveis }) {
  if (!header) return null;
  const format = (header.format || 'TEXT').toUpperCase();

  if (format === 'TEXT') {
    const txt = substituirVariaveis(header.text, variaveis);
    if (!txt) return null;
    return (
      <div className="font-semibold text-ink-1 text-sm mb-1.5 leading-tight">
        {txt}
      </div>
    );
  }

  // Mídia: placeholder visual com ícone + label.
  const config = {
    IMAGE:    { Icon: ImageIcon, label: 'Imagem',     bg: 'bg-emerald-900/30' },
    VIDEO:    { Icon: Video,     label: 'Vídeo',      bg: 'bg-brand-900/30'  },
    DOCUMENT: { Icon: FileText,  label: 'Documento',  bg: 'bg-blue-900/30'    },
    LOCATION: { Icon: MapPin,    label: 'Localização',bg: 'bg-amber-900/30'   },
  }[format];

  if (!config) return null;
  const { Icon, label, bg } = config;
  return (
    <div className={`${bg} rounded mb-1.5 flex items-center justify-center gap-2 py-4 text-ink-3`}>
      <Icon className="w-5 h-5" strokeWidth={1.6} />
      <span className="text-[11px] uppercase tracking-wider">{label}</span>
    </div>
  );
}

function ButtonRow({ button }) {
  const tipo = (button?.type || '').toUpperCase();

  if (tipo === 'QUICK_REPLY') {
    return (
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-400">
        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} />
        <span className="truncate">{button.text || 'Resposta rápida'}</span>
      </div>
    );
  }

  if (tipo === 'URL') {
    return (
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-400">
        <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} />
        <span className="truncate">{button.text || 'Abrir link'}</span>
      </div>
    );
  }

  if (tipo === 'PHONE_NUMBER') {
    return (
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-400">
        <Phone className="w-3.5 h-3.5" strokeWidth={1.8} />
        <span className="truncate">{button.text || button.phone_number || 'Ligar'}</span>
      </div>
    );
  }

  if (tipo === 'COPY_CODE' || tipo === 'OTP') {
    return (
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-400">
        <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
        <span className="truncate">{button.text || 'Copiar código'}</span>
      </div>
    );
  }

  // Tipo desconhecido — mostra texto bruto pra não esconder informação
  return (
    <div className="flex items-center justify-center px-3 py-2 text-xs text-ink-3">
      {button.text || tipo || 'Botão'}
    </div>
  );
}

/**
 * @param {{
 *   template: { components?: Array<object> } | null,
 *   variaveis?: Record<string, string>,
 *   className?: string,
 * }} props
 */
export default function TemplatePreview({ template, variaveis = {}, className = '' }) {
  if (!template) {
    return (
      <div className={`text-xs text-ink-5 italic text-center py-4 ${className}`}>
        Selecione um template para visualizar
      </div>
    );
  }

  const components = Array.isArray(template.components) ? template.components : [];
  const header = components.find((c) => c?.type === 'HEADER');
  const body   = components.find((c) => c?.type === 'BODY');
  const footer = components.find((c) => c?.type === 'FOOTER');
  const buttonsBlock = components.find((c) => c?.type === 'BUTTONS');
  const buttons = Array.isArray(buttonsBlock?.buttons) ? buttonsBlock.buttons : [];

  const bodyText = substituirVariaveis(body?.text, variaveis);

  return (
    <div className={`rounded-lg overflow-hidden border border-white/[0.08] bg-surface-1 ${className}`}>
      {/* Balão estilo WhatsApp — fundo verde-escuro discreto, borda esquerda */}
      <div className="bg-emerald-950/30 border-l-2 border-emerald-600 px-3 py-2.5">
        <HeaderBlock header={header} variaveis={variaveis} />

        <div className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed min-h-[1.25rem]">
          {bodyText || <span className="text-ink-5 italic">Sem corpo</span>}
        </div>

        {footer?.text && (
          <div className="text-[10px] text-ink-4 italic mt-1.5">
            {footer.text}
          </div>
        )}
      </div>

      {buttons.length > 0 && (
        <div className="border-t border-white/[0.06] divide-y divide-white/[0.06] bg-base">
          {buttons.map((btn, i) => (
            <ButtonRow key={`${btn?.type || 'btn'}-${i}`} button={btn} />
          ))}
        </div>
      )}
    </div>
  );
}
